class CartDrawerCrossSell extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick = (event) => {
    const arrow = event.target.closest('[data-cross-sell-scroll]');
    if (arrow && this.contains(arrow)) {
      const track = this.querySelector('[data-cross-sell-track]');
      const direction = Number(arrow.dataset.crossSellScroll || 1);
      track?.scrollBy({ left: direction * Math.max(220, track.clientWidth * 0.72), behavior: 'smooth' });
      return;
    }

    const addButton = event.target.closest('[data-cross-sell-add]');
    if (!addButton || !this.contains(addButton)) return;

    event.preventDefault();
    this.addProduct(addButton);
  };

  async addProduct(button) {
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;

    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    const root = window.Shopify?.routes?.root || '/';
    const status = this.querySelector('[data-cross-sell-status]');

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
    if (status) status.textContent = 'Agregando producto…';

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'No pudimos agregar el producto.');

      await this.refreshDrawer(root);
    } catch (error) {
      if (status) status.textContent = error.message || 'No pudimos agregar el producto.';
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
    }
  }

  async refreshDrawer(root) {
    const response = await fetch(`${root}?sections=cart-drawer-section`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error('No pudimos actualizar el carrito.');

    const sections = await response.json();
    const html = sections['cart-drawer-section'];
    if (!html) throw new Error('No pudimos actualizar el carrito.');

    const template = document.createElement('template');
    template.innerHTML = html.trim();

    const nextSection = template.content.querySelector('#shopify-section-cart-drawer-section');
    const currentSection = document.querySelector('#shopify-section-cart-drawer-section');
    if (!nextSection || !currentSection) throw new Error('No pudimos actualizar el carrito.');

    currentSection.replaceWith(nextSection);

    await customElements.whenDefined('theme-drawer');
    const drawer = document.querySelector('#cart-drawer');

    requestAnimationFrame(() => {
      if (drawer && typeof drawer.open === 'function') drawer.open();
      document.dispatchEvent(
        new CustomEvent('cart:updated', { bubbles: true, detail: { source: 'cart-cross-sell' } })
      );
      document.dispatchEvent(
        new CustomEvent('cart-drawer:open', { bubbles: true, detail: { source: 'cart-cross-sell' } })
      );
    });
  }
}

const CART_PACK_STATE_KEY = 'chclean-cart-pack-expanded';

function readCartPackState() {
  try {
    return sessionStorage.getItem(CART_PACK_STATE_KEY) === 'true';
  } catch (_error) {
    return false;
  }
}

function saveCartPackState(expanded) {
  try {
    sessionStorage.setItem(CART_PACK_STATE_KEY, String(expanded));
  } catch (_error) {
    // Storage can be unavailable in strict privacy modes; the accordion still works.
  }
}

function getCartPackRows(root = document) {
  return Array.from(
    root.querySelectorAll('#cart-drawer .cart-items__table tbody > .cart-items__table-row')
  ).filter((row) => {
    if (row.hasAttribute('data-cart-pack-item')) return true;
    return /arma\s+tu\s+pack/i.test(row.textContent || '');
  });
}

function ensureCartPackAccordion(root = document) {
  const drawer = root.querySelector('#cart-drawer') || document.querySelector('#cart-drawer');
  if (!drawer) return;

  const table = drawer.querySelector('.cart-items__table');
  const body = table?.querySelector('tbody');
  if (!table || !body) return;

  const rows = getCartPackRows(drawer);
  if (rows.length === 0) return;

  rows.forEach((row, index) => {
    row.classList.add('cart-pack__item');
    row.classList.toggle('cart-pack__item--last', index === rows.length - 1);
    row.setAttribute('data-cart-pack-item', '');
  });

  if (body.querySelector('[data-cart-pack-summary]')) return;

  const itemCount = rows.reduce((total, row) => {
    const input = row.querySelector('input[name="updates[]"], input[ref="quantityInput"]');
    const quantity = Number(input?.value || 1);
    return total + (Number.isFinite(quantity) && quantity > 0 ? quantity : 1);
  }, 0);

  const savingsElement = drawer.querySelector('.cart-drawer__breakdown-row--savings strong');
  const savingsText = (savingsElement?.textContent || '').trim().replace(/^-\s*/, '');
  const savingsMarkup = savingsText && !/^\$?0(?:\D|$)/.test(savingsText)
    ? `<span class="cart-pack__savings">Ahorras ${savingsText}</span>`
    : '<span class="cart-pack__savings"></span>';

  const summary = document.createElement('tr');
  summary.className = 'cart-pack__summary-row';
  summary.setAttribute('data-cart-pack-summary', '');
  summary.innerHTML = `
    <td colspan="4" class="cart-pack__summary-cell">
      <button class="cart-pack__toggle" type="button" data-cart-pack-toggle aria-expanded="false">
        <span class="cart-pack__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M4 8.5h16v11H4zM3 5h18v3.5H3zM12 5v14.5M8.1 5C6.5 5 5.5 4.2 5.5 3.1 5.5 2 6.4 1.5 7.3 1.7 9 2 10.4 3.5 12 5ZM15.9 5c1.6 0 2.6-.8 2.6-1.9 0-1.1-.9-1.6-1.8-1.4C15 2 13.6 3.5 12 5Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
          </svg>
        </span>
        <strong class="cart-pack__title">Pack creado</strong>
        <span class="cart-pack__count">${itemCount} ${itemCount === 1 ? 'artículo' : 'artículos'}</span>
        ${savingsMarkup}
        <span class="cart-pack__chevron" aria-hidden="true">
          <svg viewBox="0 0 20 20" fill="none">
            <path d="m6.5 8 3.5 3.5L13.5 8" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
      </button>
    </td>`;

  body.insertBefore(summary, rows[0]);
}

function setCartPackExpanded(expanded, root = document) {
  const summary = root.querySelector('[data-cart-pack-summary]');
  const toggle = root.querySelector('[data-cart-pack-toggle]');
  const items = root.querySelectorAll('[data-cart-pack-item]');
  const state = root.querySelector('.cart-pack__state');

  if (!summary || !toggle || items.length === 0) return;

  summary.classList.toggle('is-expanded', expanded);
  toggle.setAttribute('aria-expanded', String(expanded));
  if (state) state.checked = expanded;
  items.forEach((item) => {
    item.hidden = !expanded;
  });
}

function restoreCartPackState() {
  requestAnimationFrame(() => {
    ensureCartPackAccordion();
    setCartPackExpanded(readCartPackState());
  });
}

function handleCartPackToggle(event) {
  const toggle = event.target.closest('[data-cart-pack-toggle]');
  if (!toggle) return;

  event.preventDefault();
  const expanded = toggle.getAttribute('aria-expanded') !== 'true';
  saveCartPackState(expanded);
  setCartPackExpanded(expanded);
}

if (!window.__chcleanCartPackAccordionReady) {
  window.__chcleanCartPackAccordionReady = true;
  document.addEventListener('click', handleCartPackToggle);
  document.addEventListener('cart:updated', restoreCartPackState);
  document.addEventListener('cart-drawer:open', restoreCartPackState);
  document.addEventListener('shopify:cart:lines-update', (event) => {
    event.promise?.then(() => setTimeout(restoreCartPackState, 0)).catch(() => {});
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restoreCartPackState, { once: true });
  } else {
    restoreCartPackState();
  }

  const cartPackObserver = new MutationObserver((mutations) => {
    if (!mutations.some((mutation) => mutation.addedNodes.length > 0)) return;
    restoreCartPackState();
  });
  cartPackObserver.observe(document.documentElement, { childList: true, subtree: true });
}

if (!customElements.get('cart-drawer-cross-sell')) {
  customElements.define('cart-drawer-cross-sell', CartDrawerCrossSell);
}
